import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig
} from 'axios'
import { useAuthStoreWithOut } from '@/store/modules/auth'
import qs from 'qs'
import { config } from './config'
import { ElMessage } from 'element-plus'

const { result_code, unauthorized_code, request_timeout } = config
const successCode = Number(result_code)
const unauthorizedCode = Number(unauthorized_code)

type AuthRequestConfig = InternalAxiosRequestConfig & {
  _authRetry?: boolean
  _skipAuthRecovery?: boolean
}

const AUTH_RECOVERABLE_MESSAGE_PATTERNS = [
  '请您先登录',
  '未认证',
  '认证已失效',
  '登录已超时',
  '无效认证'
]
const AUTH_TERMINAL_MESSAGE_PATTERNS = ['无权限', '权限不足', '已被冻结', '冻结', '无权操作']

let refreshTokenPromise: Promise<IResponse> | null = null
let logoutPrompted = false
const MIN_PROACTIVE_REFRESH_INTERVAL_MS = 30_000
let lastTokenRefreshAt = 0

// 创建axios实例
const service: AxiosInstance = axios.create({
  baseURL: '/api', // api 的 base_url
  timeout: request_timeout, // 请求超时时间
  headers: {} // 请求头信息
})

// request拦截器
service.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const authConfig = config as AuthRequestConfig
    const authStore = useAuthStoreWithOut()
    if (!authConfig._skipAuthRecovery && !isAuthEndpoint(config.url || '')) {
      if (refreshTokenPromise) await refreshTokenPromise
    }
    const token = authStore.getToken
    ;(config.headers as any)['X-Client-Source'] = 'web-admin'
    if (token !== '' && !authConfig._skipAuthRecovery) {
      ;(config.headers as any)[authStore.getTokenKey ?? 'Authorization'] = token // 让每个请求携带自定义token 请根据实际情况自行修改
    }
    if (
      config.method === 'post' &&
      (config.headers as any)['Content-Type'] === 'application/x-www-form-urlencoded'
    ) {
      config.data = qs.stringify(config.data)
    }
    // post put 参数处理
    if (
      (config.method === 'post' || config.method === 'put') &&
      (config.headers as any)['Content-Type'] === 'application/json'
    ) {
      for (const key in config.data) {
        // 参数处理
        if (config.data[key] === '') {
          config.data[key] = null
        }
      }
    }
    // get参数编码（数组会序列化为 key=1&key=2，便于后端 list 参数接收）
    if (config.method === 'get' && config.params) {
      let url = config.url as string
      url += '?'
      const keys = Object.keys(config.params)
      for (const key of keys) {
        const val = config.params[key]
        if (val === void 0 || val === null || val === '') continue
        if (Array.isArray(val)) {
          for (const v of val) {
            if (v !== void 0 && v !== null && v !== '') {
              url += `${key}=${encodeURIComponent(v)}&`
            }
          }
        } else {
          url += `${key}=${encodeURIComponent(val)}&`
        }
      }
      url = url.substring(0, url.length - 1)
      config.params = {}
      config.url = url
    }
    return config
  },
  (error: AxiosError) => {
    // Do something with request error
    console.log('请求报错', error) // for debug
    return Promise.reject(error)
  }
)

function hasAnyPattern(message = '', patterns: string[] = []) {
  return patterns.some((pattern) => message.includes(pattern))
}

function isAuthEndpoint(url = '') {
  return ['/auth/login', '/auth/token/refresh', '/auth/refresh_token'].some((path) =>
    url.includes(path)
  )
}

function extractResponsePayload(response?: AxiosResponse<any>) {
  return response?.data || {}
}

function extractResponseMessage(payload: any = {}, fallback = '') {
  const message = payload.message || payload.msg || payload.detail
  return typeof message === 'string' && message.trim() ? message : fallback || ''
}

function isTerminalAuthFailure(message = '') {
  return hasAnyPattern(message, AUTH_TERMINAL_MESSAGE_PATTERNS)
}

function isRecoverableAuthFailure(message = '') {
  return hasAnyPattern(message, AUTH_RECOVERABLE_MESSAGE_PATTERNS)
}

function shouldAttemptAuthRecoveryByResponse(response: AxiosResponse<any>) {
  const requestConfig = response.config as AuthRequestConfig
  if (requestConfig._skipAuthRecovery || requestConfig._authRetry) return false
  if (isAuthEndpoint(requestConfig.url || '')) return false

  const payload = extractResponsePayload(response)
  const code = Number(payload.code || 0)
  const message = extractResponseMessage(payload)

  if (isTerminalAuthFailure(message)) return false
  if (code === unauthorizedCode) return true
  if (code === 403) return isRecoverableAuthFailure(message)
  return isRecoverableAuthFailure(message)
}

function shouldAttemptAuthRecoveryByError(error: AxiosError<any>) {
  const requestConfig = (error.config || {}) as AuthRequestConfig
  if (requestConfig._skipAuthRecovery || requestConfig._authRetry) return false
  if (isAuthEndpoint(requestConfig.url || '')) return false

  const status = error.response?.status
  const payload = extractResponsePayload(error.response)
  const code = Number(payload.code || 0)
  const message = extractResponseMessage(payload, error.message)

  if (isTerminalAuthFailure(message)) return false
  if (status === 401 || code === unauthorizedCode) return true
  if (status === 403 || code === 403) return isRecoverableAuthFailure(message)
  return isRecoverableAuthFailure(message)
}

function isAuthFailureResponse(response?: AxiosResponse<any>, fallback = '') {
  if (!response) return false
  const payload = extractResponsePayload(response)
  const message = extractResponseMessage(payload, fallback)
  if (isTerminalAuthFailure(message)) return false
  return (
    Number(payload.code) === unauthorizedCode ||
    response.status === 401 ||
    isRecoverableAuthFailure(message)
  )
}

function applyToken(res: IResponse) {
  const authStore = useAuthStoreWithOut()
  authStore.setToken(`${res.data.token_type} ${res.data.access_token}`)
  authStore.setRefreshToken(res.data.refresh_token)
  lastTokenRefreshAt = Date.now()
}

function logoutForAuthFailure(message = '认证已失效，请重新登录') {
  if (logoutPrompted) return
  logoutPrompted = true
  const authStore = useAuthStoreWithOut()
  authStore.logout(message)
  setTimeout(() => {
    logoutPrompted = false
  }, 1500)
}

function ensureTokenRefreshed() {
  if (refreshTokenPromise) {
    return refreshTokenPromise
  }

  refreshTokenPromise = refreshToken()
    .then((res) => {
      applyToken(res)
      return res
    })
    .catch((error) => {
      logoutForAuthFailure()
      return Promise.reject(error)
    })
    .finally(() => {
      refreshTokenPromise = null
    })

  return refreshTokenPromise
}

function refreshTokenIfDue() {
  if (refreshTokenPromise) return
  if (Date.now() - lastTokenRefreshAt < MIN_PROACTIVE_REFRESH_INTERVAL_MS) return
  ensureTokenRefreshed().catch(() => {
    // 主请求已成功，后台刷新失败时由统一退出逻辑处理。
  })
}

async function retryOriginalRequest<T = any>(originalConfig: AuthRequestConfig): Promise<T> {
  const authStore = useAuthStoreWithOut()
  // 迟到的旧 token 响应直接使用已经更新的 token，避免再次刷新。
  const sentToken = originalConfig.headers?.[authStore.getTokenKey ?? 'Authorization']
  if (refreshTokenPromise || !authStore.getToken || sentToken === authStore.getToken) {
    await ensureTokenRefreshed()
  }
  originalConfig._authRetry = true
  originalConfig.headers = originalConfig.headers || ({} as any)
  ;(originalConfig.headers as any)[authStore.getTokenKey ?? 'Authorization'] = authStore.getToken
  return service.request(originalConfig) as Promise<T>
}

// response 拦截器
service.interceptors.response.use(
  (response: AxiosResponse<any>) => {
    // 这个状态码是和后端约定好的
    const code = Number(response.data.code || unauthorizedCode)
    const message = response.data.message || '后端接口无返回内容'
    const refresh = response.headers['if-refresh']
    const requestConfig = response.config as AuthRequestConfig

    if (response.config.responseType === 'blob') {
      // 如果是文件流，直接过
      return response
    } else if (requestConfig._skipAuthRecovery && code !== successCode) {
      return Promise.reject(new Error(message))
    } else if (code === successCode) {
      if (refresh === '1') {
        // 主动续期做最小间隔保护，避免过期判定异常时每个请求都刷新。
        refreshTokenIfDue()
      }
      return response.data
    } else if (requestConfig._authRetry && isAuthFailureResponse(response)) {
      logoutForAuthFailure()
      return Promise.reject(new Error(message))
    } else if (shouldAttemptAuthRecoveryByResponse(response)) {
      // 因token无效，token过期导致
      return retryOriginalRequest(response.config as AuthRequestConfig)
    } else {
      ElMessage.error(message)
      return Promise.reject(new Error(message))
    }
  },
  async (error: AxiosError<any>) => {
    console.log('err', error)
    const responseMessage = extractResponseMessage(extractResponsePayload(error.response))
    let message = responseMessage || error.message
    const authStore = useAuthStoreWithOut()
    const status = error.response?.status
    const requestConfig = (error.config || {}) as AuthRequestConfig

    if (requestConfig._skipAuthRecovery && (status === 401 || status === 403)) {
      return Promise.reject(error)
    }

    if (shouldAttemptAuthRecoveryByError(error)) {
      return retryOriginalRequest(error.config as AuthRequestConfig)
    }

    if (requestConfig._authRetry && isAuthFailureResponse(error.response, responseMessage)) {
      logoutForAuthFailure()
      return Promise.reject(error)
    }

    switch (status) {
      case 400:
        message = responseMessage || '请求错误'
        break
      case 401:
        // 强制要求重新登录，因账号已冻结，账号已过期，手机号码错误，刷新token无效等问题导致
        authStore.logout('认证已失效，请重新登录')
        message = '认证已失效，请重新登录'
        break
      case 403:
        // 真正无系统权限，不刷新 token，也不强制退出登录
        message = '无权限访问，请联系管理员'
        break
      case 404:
        message = `请求地址出错: ${error.response?.config.url}`
        break
      case 408:
        message = '请求超时'
        break
      case 500:
        message = '服务器内部错误'
        break
      case 501:
        message = '服务未实现'
        break
      case 502:
        message = '网关错误'
        break
      case 503:
        message = '服务不可用'
        break
      case 504:
        message = '网关超时'
        break
      case 505:
        message = 'HTTP版本不受支持'
        break
      default:
        break
    }
    ElMessage.error(message)
    return Promise.reject(error)
  }
)

// 刷新Token
const refreshToken = (): Promise<IResponse> => {
  const authStore = useAuthStoreWithOut()
  const data = authStore.getRefreshToken
  if (!data) {
    return Promise.reject(new Error('缺少 refresh token'))
  }
  return service.post('/auth/token/refresh', data, {
    _skipAuthRecovery: true
  } as AxiosRequestConfig) as unknown as Promise<IResponse>
}

export { service }
